import type { Entry } from 'contentful';
import type { IContentPage } from '~/lib/ContentfulService';
import { isContentPage } from '~/utils/guards/isContentPage';
import { isDefined } from '~/utils/guards/isDefined';
import { buildUrlString } from '~/utils/buildUrlString';
import { usePageTitleState } from '~/composables/usePageTitleState';
import { useSeoAlternateUrls } from '~/composables/useSeoAlternateUrls';
import { useHostname } from '~/composables/useHostname';

export function calculateCanonical(
  canonical: Entry<unknown> | undefined,
  locale?: string
): string | undefined {
  if (!isContentPage(canonical)) {
    return;
  }
  const slug = canonical?.fields?.slug;

  if (!slug) {
    return;
  }

  return buildUrlString(
    locale ?? canonical.sys.locale,
    slug,
    undefined,
    undefined,
    true
  );
}

export function calculateRobots(page?: IContentPage) {
  return [page?.fields?.seoRobotsIndexTag, page?.fields?.seoMetaRobotsFollowTag]
    .filter(isDefined)
    .join(',');
}

export function calculateHead(
  page: IContentPage | undefined,
  companyName: string,
  canonical: string,
  fallbackLocale?: string
) {
  // Die Bilder funktionieren nicht auf den Sozialen Medien, weil das https: fehlt, dadurch wird die falsche HostAdresse benutzt.
  // Der fix gehört zu Ticket: WEB2-2310
  let ogImageUrl = page?.fields?.teaserImage?.fields?.image?.fields?.file?.url;
  const httpsPrefix = 'https:';
  const imageSettings = '?w=1200&fit=pad&bg=rgb:ffffff&q=80&fm=jpg';
  if (isDefined(ogImageUrl) && ogImageUrl?.startsWith('//')) {
    ogImageUrl = `${httpsPrefix}${ogImageUrl}${imageSettings}`;
  }

  return {
    title: page?.fields?.seoTitle
      ? page?.fields?.seoTitle + ' | ' + companyName
      : companyName,
    htmlAttrs: {
      lang: page?.sys.locale ?? fallbackLocale
    },
    meta: [
      {
        name: 'description',
        content: page?.fields?.seoDescription?.trim() ?? ''
      },
      {
        name: 'robots',
        content: calculateRobots(page)
      },
      {
        property: 'og:type',
        content: 'website'
      },
      {
        property: 'og:url',
        content: canonical
      },
      {
        property: 'og:title',
        content: page?.fields?.teaserTitle?.trim() ?? ''
      },
      {
        property: 'og:description',
        content: page?.fields?.teaserText?.trim() ?? ''
      },
      {
        property: 'og:image',
        content: ogImageUrl ?? undefined
      }
    ]
  };
}

export function useMetadata(
  page: IContentPage | undefined,
  companyName: string
) {
  const route = useRoute();
  const serverUrlOrigin = useHostname();
  const locale = useLocale();
  const pageTitle = usePageTitleState();

  const canonical =
    calculateCanonical(page?.fields?.seoCanonicalTag) ??
    serverUrlOrigin + route.path ??
    '';
  const head = calculateHead(page, companyName, canonical, locale.value);

  useHead(head);

  // necessary to be set in analytics
  pageTitle.value = head.title;

  if (page?.fields.seoRobotsIndexTag !== 'NOINDEX') {
    useSeoAlternateUrls();

    useHead({
      link: [
        {
          rel: 'canonical',
          href: canonical
        }
      ]
    });
  }
}
